import { useTheme, Box, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router';
import { useMemo } from 'react';
import { Business_Profile_Enum } from '@kheops/graphql';
import { orderAtom } from '../state/state';
import { currentContextAtom } from '../../state';
import ContractsUtils from '../../common/utils/contracts.utils';
import { RoutePaths } from '../../routes/AppRoutes';
import SizedImage from '../../common/components/SizedImage';
import FileUtils, { PhotoSizeFit } from '../../common/utils/file.utils';
import BackButton from '../../common/components/BackButton';
import { getCompanyShortName } from '../../common/utils/common.utils';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import CompanyLogo from '../../common/components/CompanyLogo';
import OrderByTrustedSupplierChip from '../list/OrderByTrustedSupplierChip';
import TrustedIcon from '../../assets/icons/trusted_icon.svg?react';
import { ellipsisStyle } from '../../common/utils/style.utils';
import { isOrderSuggestedByStaff } from '../../common/utils/order.utils';
import OrderTaxFreeTotalPriceDisplay from './OrderTaxFreeTotalPriceDisplay';
import OrderSuggestionChip from './OrderSuggestionChip';

export interface OrderHeaderProps {
  compact?: boolean;
}

export default function OrderHeader({ compact }: OrderHeaderProps): React.JSX.Element {
  const { realm } = useAtomValue(currentContextAtom);
  const currencyFormat = useCurrencyFormat();
  const { t } = useTranslation(['order', 'price']);
  const theme = useTheme();
  const order = useAtomValue(orderAtom);

  const otherCompanyRealm = useMemo(
    () => (realm === Business_Profile_Enum.Buyer ? Business_Profile_Enum.Supplier : Business_Profile_Enum.Buyer),
    [realm],
  );

  const otherCompany = ContractsUtils.getOtherCompany(order.contract, realm);
  const companyLink = useMemo(() => {
    return generatePath(RoutePaths.CONTRACT_COMPANY, { companyId: otherCompany.id });
  }, [otherCompany]);

  const companyImage = useMemo(() => {
    const size = compact
      ? { width: 112, height: 112, fit: 'cover' as PhotoSizeFit }
      : { width: 389, height: 156, fit: 'cover' as PhotoSizeFit };

    return {
      photo: otherCompany.photos[0]?.photo,
      type: otherCompanyRealm,
      size,
    };
  }, [otherCompany, compact]);

  const shouldDisplayTrustedSupplierFeedback = useMemo(() => (
    order.contract.is_trusted_supplier && realm === Business_Profile_Enum.Buyer && order.suggestor_id
  ), [order, realm]);

  const isKheopsSuggestion = useMemo(() => (
    realm === Business_Profile_Enum.Supplier && isOrderSuggestedByStaff(order)
  ), [order, realm]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: compact ? 'row' : 'column',
            gap: 2,
            position: 'relative',
          }}
        >
          {!compact && (
            <SizedImage
              src={FileUtils.BuildCompanyImageUrl(companyImage)}
              width="100%"
              sx={{
                '& img:not([class^="Kheops-companyLogo"]):not([class*=" Kheops-companyLogo"])': {
                  maxHeight: 'unset',
                  aspectRatio: '3 / 1',
                  objectFit: 'cover',
                },
              }}
              borderRadius={0}
              alt={t('common:company')}
              overlay={(
                <>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: theme.spacing(2),
                      left: theme.spacing(2),
                      zIndex: 10,
                    }}
                  >
                    <BackButton variant="tonal" to={RoutePaths.ORDER_LIST} compact />
                  </Box>
                  {order.suggestor_id && (
                    <Box sx={{ position: 'absolute', bottom: theme.spacing(2), left: theme.spacing(2) }}>
                      {shouldDisplayTrustedSupplierFeedback ? <OrderByTrustedSupplierChip /> : <OrderSuggestionChip isKheopsSuggestion={isKheopsSuggestion} />}
                    </Box>
                  )}
                  {otherCompany.brand && (
                    <CompanyLogo
                      brand={otherCompany.brand}
                      variant="square"
                      size="large"
                      sx={{
                        width: 64,
                        position: 'absolute',
                        right: theme.spacing(1.5),
                        bottom: theme.spacing(1.5),
                      }}
                    />
                  )}
                </>
              )}
            />
          )}
          {compact && (
            <Link to={companyLink}>
              <SizedImage
                src={FileUtils.BuildCompanyImageUrl(companyImage)}
                width={88}
                borderRadius={4}
                height={88}
                alt={t('common:company')}
                overlay={
                  otherCompany.brand && (
                    <CompanyLogo
                      brand={otherCompany.brand}
                      size="medium"
                      variant="square"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                      }}
                    />
                  )
                }
              />
            </Link>
          )}
          <Box sx={{ display: 'flex', px: compact ? 0 : 2, gap: 2, flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                flex: 1,
              }}
            >
              <Typography
                color="onTertiaryContainer"
                variant="labelSmall"
                sx={{ width: 'fit-content', backgroundColor: 'tertiaryContainer.main' }}
              >
                # {order.friendly_id}
              </Typography>
              <Box sx={{ display: 'grid', alignItems: 'center', gap: 0.5, gridTemplateColumns: 'auto 1fr' }}>
                {shouldDisplayTrustedSupplierFeedback && compact && (<TrustedIcon width={20} height={20} />)}
                <Typography
                  variant={compact ? 'bodyLarge' : 'titleLarge'}
                  component={Link}
                  to={companyLink}
                  sx={{
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                    ...(compact ? ellipsisStyle : undefined),
                  }}
                >
                  {getCompanyShortName(otherCompany.tradeName, otherCompanyRealm)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <OrderTaxFreeTotalPriceDisplay />
                <Typography variant="bodySmall" color="onSurfaceVariant">{t('price:price_tax_included')}: {currencyFormat.format(order.total_price_to_pay_incl_tax!)}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
